import { useState } from "react";
import { Button, Group, Modal, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons-react";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { useStorage } from "../../helpers/firebaseContext";
import { CtcFilePicker } from "./ctc_file_picker";
import { useCtcCallables } from "./ctc_helpers";
import { CTC_MAX_ADDED_ATTACHMENTS } from "../../../../shared/ctcAccess";

/*
 * Add attachments to an order (order page → Documents): any additional files, shown as "Other document". The files go to
 * ctc/{poId}/other_* and ctcAddAttachments checks each one like a dealer upload (uploader, real content) and adds it
 * to po.attachments (number CTC_OTHER_DOCUMENT_NUMBER). Anyone working on the order may add them (not view-only users).
 */

/** "Add documents" button: pick several files, remove any before uploading, then upload and check them all. */
export function CtcAddAttachmentsButton({ poId, email }: { poId: string; email: string }) {
    const storage = useStorage();
    const { addAttachments } = useCtcCallables();
    const [open, setOpen] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [busy, setBusy] = useState(false);

    const upload = async () => {
        setBusy(true);
        try {
            const paths: string[] = [];
            for (const [i, file] of files.entries()) {
                const safe = file.name.replace(/[^\w.-]+/g, "_").slice(-80);
                const path = `ctc/${poId}/other_${Date.now()}_${i}_${safe}`;
                await uploadBytes(storageRef(storage, path), file, { contentType: file.type, customMetadata: { uploadedBy: email, originalName: file.name } });
                paths.push(path);
            }
            // The server checks every file (a file that isn't really a PDF / JPG / PNG is deleted) and lists them
            const { data } = await addAttachments({ poId, paths });
            notifications.show({ color: "green", message: `${data.added} document${data.added === 1 ? "" : "s"} added` });
            setFiles([]);
            setOpen(false);
        } catch (e) {
            notifications.show({ color: "red", message: `Upload failed: ${(e as Error).message}` });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button size="compact-xs" variant="light" leftSection={<IconUpload size={14} />} onClick={() => setOpen(true)}>Add documents</Button>
            <Modal opened={open} onClose={() => !busy && setOpen(false)} title={<Text fw={600}>Add documents</Text>} size="lg">
                <Stack>
                    <Text size="sm" c="dimmed">Files can't be replaced or deleted after upload.</Text>
                    <CtcFilePicker label="Documents" files={files} maxFiles={CTC_MAX_ADDED_ATTACHMENTS} disabled={busy} onChange={setFiles} />
                    <Group justify="flex-end">
                        <Button variant="default" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
                        <Button loading={busy} disabled={!files.length} onClick={upload}>Upload {files.length || ""}</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}
